import type { RadioStation } from './radios';

export interface RadioProfile {
  frequency: string;
  city: string;
  country: string;
  foundedYear: number;
  history: string;
  mission: string;
  vision: string;
  categories: string[];
  languages: string[];
  schedule: ScheduleEntry[];
}

export interface ScheduleEntry {
  day: string;
  timeRange: string;
  program: string;
  host: string;
}

export interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  photoUrl: string;
  social?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
  };
}

export interface RadioTeam {
  director: TeamMember;
  locutores: TeamMember[];
  productores: TeamMember[];
  invitados: TeamMember[];
}

export const radioProfiles: Record<number, RadioProfile> = {
  1: {
    frequency: '94.70 RFW',
    city: 'Santa Rosa, California',
    country: 'Estados Unidos',
    foundedYear: 2015,
    history: 'Nació como un proyecto de comunidad en 2015, cuando un grupo de amigos apasionados por la música decidió crear un espacio donde las voces locales tuvieran eco. Lo que comenzó como una pequeña transmisión desde un garage en Santa Rosa, se convirtió en una de las estaciones web más escuchadas del área de la Bahía. En 2018, inauguraron sus estudios profesionales y lanzaron su aplicación móvil, alcanzando una audiencia de más de 50,000 oyentes mensuales. Hoy, Nueva Actitud es sinónimo de música pop contemporánea, entrevistas exclusivas y una conexión genuina con su comunidad.',
    mission: 'Conectar personas a través de la música y el entretenimiento, creando un espacio inclusivo donde cada oyente se sienta parte de una familia que celebra la cultura pop y la creatividad local.',
    vision: 'Ser la radio web líder en contenido pop en español e inglés en Estados Unidos, expandiendo nuestra señal a comunidades latinas de todo el país y apoyando el talento emergente de la región.',
    categories: ['Pop', 'Top 40', 'Entretenimiento', 'Entrevistas', 'Eventos'],
    languages: ['Español', 'Inglés'],
    schedule: [
      { day: 'Lunes - Viernes', timeRange: '06:00 - 10:00', program: 'Despierta con Energía', host: 'Andrea Castillo' },
      { day: 'Lunes - Viernes', timeRange: '10:00 - 14:00', program: 'Mañana Activa', host: 'Valentina Herrera' },
      { day: 'Lunes - Viernes', timeRange: '14:00 - 19:00', program: 'Tardes de Pop', host: 'Carlos Mendoza' },
      { day: 'Lunes - Viernes', timeRange: '19:00 - 23:00', program: 'Noche de Estrenos', host: 'Valentina Herrera' },
      { day: 'Sábados', timeRange: '08:00 - 12:00', program: 'Sábado Retro', host: 'Carlos Mendoza' },
      { day: 'Sábados', timeRange: '12:00 - 18:00', program: 'Festival en Vivo', host: 'Equipo Rotativo' },
      { day: 'Domingos', timeRange: '10:00 - 14:00', program: 'Domingo Familiar', host: 'Andrea Castillo' },
    ],
  },
  2: {
    frequency: '98.20 RFW',
    city: 'Miami, Florida',
    country: 'Estados Unidos',
    foundedYear: 2010,
    history: 'Radio Fe y Esperanza nació en 2010 de la mano de un grupo de creyentes que buscaban llevar un mensaje de esperanza a través de las ondas. Desde una pequeña emisora comunitaria en Miami, han crecido hasta convertirse en una de las radios cristianas web más seguidas del sureste. Su fundador, el Pastor Daniel Vega, soñaba con un espacio donde la fe y la música se encontraran. Hoy, miles de familias sintonizan cada día para encontrar aliento, música cristiana y mensajes que transforman vidas.',
    mission: 'Llevar un mensaje de esperanza, fe y amor a través de la radio, fortaleciendo familias y comunidades con contenido inspirador y música que eleva el alma.',
    vision: 'Ser la voz de la esperanza para la comunidad hispana en Estados Unidos, expandiendo nuestra señal hasta llegar a cada hogar que necesite un mensaje de fe.',
    categories: ['Cristiana', 'Alabanza', 'Reflexión', 'Oración', 'Familia'],
    languages: ['Español'],
    schedule: [
      { day: 'Lunes - Viernes', timeRange: '05:00 - 08:00', program: 'Amanecer con Fe', host: 'Pastor Daniel Vega' },
      { day: 'Lunes - Viernes', timeRange: '08:00 - 12:00', program: 'Mañana de Alabanza', host: 'Elena Ruiz' },
      { day: 'Lunes - Viernes', timeRange: '12:00 - 15:00', program: 'Meditaciones', host: 'Pastor Daniel Vega' },
      { day: 'Lunes - Viernes', timeRange: '15:00 - 19:00', program: 'Tardes de Esperanza', host: 'Elena Ruiz' },
      { day: 'Lunes - Viernes', timeRange: '19:00 - 22:00', program: 'Noche de Oración', host: 'Equipo Pastoral' },
      { day: 'Domingos', timeRange: '07:00 - 12:00', program: 'Servicio Dominical', host: 'Pastor Daniel Vega' },
      { day: 'Domingos', timeRange: '18:00 - 21:00', program: 'Reflexiones de Fe', host: 'Equipo Pastoral' },
    ],
  },
  3: {
    frequency: '103.50 RFW',
    city: 'Houston, Texas',
    country: 'Estados Unidos',
    foundedYear: 2012,
    history: 'Fundada en 2012 por un grupo de músicos mexicanos residentes en Houston, La Mexicana FM surgió con la idea de mantener viva la cultura musical de México en el extranjero. Comenzaron transmitiendo desde un local en el barrio latino, y rápidamente se ganaron el corazón de la comunidad mexicana. En 2016, abrieron sus estudios oficiales y comenzaron a cubrir eventos culturales en vivo. Hoy son la referencia obligada para la música regional mexicana en Texas, con una audiencia fiel que crece cada día.',
    mission: 'Preservar y celebrar la música y cultura mexicana en Estados Unidos, conectando a la comunidad latina con sus raíces a través de la mejor música regional.',
    vision: 'Ser la radio mexicana número uno en Estados Unidos, llevando el orgullo de la música regional a cada rincón donde haya un mexicano lejos de casa.',
    categories: ['Regional Mexicana', 'Banda', 'Norteño', 'Mariachi', 'Cultura'],
    languages: ['Español'],
    schedule: [
      { day: 'Lunes - Viernes', timeRange: '06:00 - 10:00', program: 'Despierta con Sabor', host: 'Alejandro Ramírez' },
      { day: 'Lunes - Viernes', timeRange: '10:00 - 14:00', program: 'El Sabor del Mediodía', host: 'Alejandro Ramírez' },
      { day: 'Lunes - Viernes', timeRange: '14:00 - 18:00', program: 'Tardes de Banda', host: 'María Flores' },
      { day: 'Lunes - Viernes', timeRange: '18:00 - 22:00', program: 'Noche Norteña', host: 'María Flores' },
      { day: 'Sábados', timeRange: '08:00 - 14:00', program: 'Sábado de Fiesta', host: 'Alejandro Ramírez' },
      { day: 'Sábados', timeRange: '14:00 - 20:00', program: 'Mariachi en Vivo', host: 'Invitados Especiales' },
      { day: 'Domingos', timeRange: '10:00 - 14:00', program: 'Domingo Familiar', host: 'María Flores' },
    ],
  },
  5: {
    frequency: '87.01 RFW',
    city: 'Los Ángeles, California',
    country: 'Estados Unidos',
    foundedYear: 2018,
    history: 'Rock Total nació en 2018 del underground rockero de Los Ángeles. Diana Ríos, una apasionada del rock desde los 15, reunió a un grupo de amigos para crear una radio que sonara diferente. Sin patrocinadores, sin presupuesto, pero con mucha actitud. La emisora creció boca a boca entre los fanáticos del rock. En 2021, lograron su primer patrocinador y abrieron su bunker de estudios en el centro. Hoy, Rock Total es el hogar de la escena rockera angelina y una plataforma para bandas emergentes.',
    mission: 'Difundir el rock en todas sus formas, apoyando a bandas emergentes y creando una comunidad de fanáticos que viven y respiran rock.',
    vision: 'Convertirse en la radio de referencia para el rock independiente en Estados Unidos, descubriendo y promoviendo el talento de la próxima generación de rockeros.',
    categories: ['Rock', 'Rock Alternativo', 'Metal', 'Indie', 'Conciertos'],
    languages: ['Español', 'Inglés'],
    schedule: [
      { day: 'Lunes - Viernes', timeRange: '07:00 - 11:00', program: 'Rock Mañanero', host: 'Diana Ríos' },
      { day: 'Lunes - Viernes', timeRange: '11:00 - 15:00', program: 'Clásicos del Rock', host: 'Kevin Stone' },
      { day: 'Lunes - Viernes', timeRange: '15:00 - 19:00', program: 'Rock Total en Vivo', host: 'Diana Ríos' },
      { day: 'Lunes - Viernes', timeRange: '19:00 - 23:00', program: 'Noche de Metal', host: 'Kevin Stone' },
      { day: 'Sábados', timeRange: '10:00 - 14:00', program: 'Sábado de Conciertos', host: 'Diana Ríos' },
      { day: 'Sábados', timeRange: '14:00 - 20:00', program: 'Indie Sessions', host: 'Invitados Especiales' },
      { day: 'Domingos', timeRange: '12:00 - 16:00', program: 'Domingo de Clásicos', host: 'Kevin Stone' },
    ],
  },
  7: {
    frequency: '92.45 RFW',
    city: 'Dallas, Texas',
    country: 'Estados Unidos',
    foundedYear: 2016,
    history: 'Deportes Total nació en 2016 cuando Fernando Gutiérrez, un exjugador de fútbol convertido en periodista, decidió crear un espacio donde el análisis deportivo fuera honesto y apasionado. Comenzó con un podcast semanal desde su casa en Dallas, y la respuesta fue tan abrumadora que decidió lanzar una radio web. En 2019, formó La Mesa Redonda, su panel de expertos que se convirtió en el programa insignia. Hoy, Deportes Total cubre eventos deportivos en vivo y tiene una de las comunidades más activas de oyentes deportivos.',
    mission: 'Ofrecer análisis deportivo honesto, apasionado y de calidad, conectando a los fanáticos con el deporte de una manera auténtica y cercana.',
    vision: 'Ser la radio deportiva líder en Estados Unidos, reconocida por la calidad de su análisis y la pasión de su equipo.',
    categories: ['Deportes', 'Fútbol', 'Análisis', 'Entrevistas', 'Cobertura en Vivo'],
    languages: ['Español'],
    schedule: [
      { day: 'Lunes - Viernes', timeRange: '06:00 - 09:00', program: 'Despertar Deportivo', host: 'Fernando Gutiérrez' },
      { day: 'Lunes - Viernes', timeRange: '09:00 - 12:00', program: 'La Mañana del Deporte', host: 'Sofía Martínez' },
      { day: 'Lunes - Viernes', timeRange: '12:00 - 15:00', program: 'Al Mediodía', host: 'Memo López' },
      { day: 'Lunes - Viernes', timeRange: '15:00 - 18:00', program: 'La Mesa Redonda', host: 'Fernando Gutiérrez' },
      { day: 'Lunes - Viernes', timeRange: '18:00 - 21:00', program: 'Noche de Champions', host: 'Equipo Deportivo' },
      { day: 'Sábados', timeRange: '08:00 - 12:00', program: 'Sábado de Fútbol', host: 'Fernando Gutiérrez' },
      { day: 'Domingos', timeRange: '10:00 - 14:00', program: 'Análisis de la Jornada', host: 'La Mesa Redonda' },
    ],
  },
};

export const radioTeams: Record<number, RadioTeam> = {
  1: {
    director: {
      id: 1,
      name: 'Roberto Castillo',
      role: 'Director General',
      bio: 'Fundador de Radio Nueva Actitud. Con más de 15 años en la industria radial, Roberto ha liderado el crecimiento de la estación desde un garage hasta una de las radios web más importantes de California.',
      photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20male%20executive%20director%20portrait%2C%20confident%20friendly%20smile%2C%20wearing%20business%20casual%20navy%20blazer%2C%20clean%20modern%20office%20background%2C%20warm%20studio%20lighting%2C%20corporate%20editorial%20headshot%20photography&width=400&height=400&seq=team-director-1&orientation=squarish',
      social: { instagram: '@robertocastillo', facebook: 'facebook.com/robertocastillo' },
    },
    locutores: [
      {
        id: 2,
        name: 'Carlos Mendoza',
        role: 'Locutor Principal',
        bio: 'La voz de las tardes. Carlos lleva 8 años en la cabina y se ha ganado el cariño de la audiencia con su humor, energía y conexión genuina con los oyentes.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Hispanic%20male%20radio%20host%20in%20professional%20broadcast%20studio%2C%20warm%20friendly%20smile%2C%20wearing%20casual%20stylish%20shirt%20and%20headphones%2C%20modern%20studio%20background%20with%20warm%20golden%20lighting%2C%20editorial%20portrait%20photography%20style&width=400&height=400&seq=team-host-1&orientation=squarish',
        social: { instagram: '@carlosmendoza', facebook: 'facebook.com/carlosmendoza' },
      },
      {
        id: 3,
        name: 'Valentina Herrera',
        role: 'Locutora',
        bio: 'Especialista en música pop internacional. Valentina trae las últimas tendencias musicales y entrevistas exclusivas con artistas del momento.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Hispanic%20female%20radio%20host%20with%20confident%20friendly%20smile%2C%20modern%20casual%20style%2C%20headphones%20around%20neck%2C%20colorful%20studio%20background%20with%20warm%20lighting%2C%20professional%20media%20portrait%20photography&width=400&height=400&seq=team-host-2&orientation=squarish',
        social: { instagram: '@valenherrera', facebook: 'facebook.com/valenherrera' },
      },
      {
        id: 4,
        name: 'Andrea Castillo',
        role: 'Locutora Morning Show',
        bio: 'La encargada de despertar a la audiencia con energía. Andrea combina noticias, humor y la mejor música para arrancar el día con buen pie.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Cheerful%20young%20Hispanic%20female%20radio%20presenter%20with%20bright%20smile%2C%20casual%20modern%20attire%2C%20vibrant%20studio%20background%20with%20colorful%20lighting%2C%20energetic%20and%20warm%20media%20portrait%20photography&width=400&height=400&seq=team-host-3&orientation=squarish',
        social: { instagram: '@andreacastillo', facebook: 'facebook.com/andreacastillo' },
      },
    ],
    productores: [
      {
        id: 5,
        name: 'Luis Martínez',
        role: 'Productor Ejecutivo',
        bio: 'El cerebro detrás de la programación. Luis coordina los contenidos, concursos y eventos especiales que hacen de Nueva Actitud una experiencia única.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20male%20media%20producer%20portrait%2C%20creative%20confident%20look%2C%20wearing%20casual%20smart%20attire%2C%20modern%20studio%20background%20with%20equipment%20visible%2C%20warm%20lighting%2C%20creative%20industry%20headshot%20photography&width=400&height=400&seq=team-producer-1&orientation=squarish',
        social: { instagram: '@luismartinezprod', facebook: 'facebook.com/luismartinezprod' },
      },
      {
        id: 6,
        name: 'Camila Torres',
        role: 'Productora de Contenido',
        bio: 'Encargada de las redes sociales y contenido digital. Camila da vida a la comunidad online de Nueva Actitud con creatividad y pasión.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Young%20female%20content%20creator%20portrait%2C%20creative%20stylish%20look%2C%20modern%20casual%20attire%2C%20bright%20studio%20background%20with%20warm%20lighting%2C%20social%20media%20professional%20headshot%20photography&width=400&height=400&seq=team-producer-2&orientation=squarish',
        social: { instagram: '@camilatorres', facebook: 'facebook.com/camilatorres' },
      },
    ],
    invitados: [
      {
        id: 7,
        name: 'DJ Marco',
        role: 'DJ Invitado',
        bio: 'DJ residente de los eventos especiales. Marco pone el ambiente en cada festival y transmisión en vivo con su mezcla única de éxitos.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Male%20DJ%20portrait%20with%20stylish%20urban%20look%2C%20confident%20expression%2C%20wearing%20trendy%20streetwear%2C%20nightclub%20atmosphere%20background%20with%20colorful%20lights%2C%20creative%20music%20industry%20portrait%20photography&width=400&height=400&seq=team-guest-1&orientation=squarish',
        social: { instagram: '@djmarco', facebook: 'facebook.com/djmarco' },
      },
      {
        id: 8,
        name: 'Sofía Reyes',
        role: 'Artista Invitada',
        bio: 'Cantante emergente que ha colaborado en sesiones acústicas en vivo. Su voz y carisma han conquistado a la audiencia de Nueva Actitud.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Young%20female%20singer%20portrait%20with%20artistic%20elegant%20look%2C%20warm%20smile%2C%20creative%20makeup%2C%20soft%20studio%20lighting%20with%20warm%20tones%2C%20music%20artist%20promotional%20headshot%20photography&width=400&height=400&seq=team-guest-2&orientation=squarish',
        social: { instagram: '@sofiareyesmusic', facebook: 'facebook.com/sofiareyesmusic' },
      },
    ],
  },
  2: {
    director: {
      id: 1,
      name: 'Pastor Daniel Vega',
      role: 'Director y Fundador',
      bio: 'Fundador de Radio Fe y Esperanza. Pastor con 20 años de experiencia en el ministerio, dedicado a llevar mensajes de fe y esperanza a través de los medios.',
      photoUrl: 'https://readdy.ai/api/search-image?query=Spiritual%20male%20pastor%20portrait%20with%20warm%20gentle%20expression%2C%20peaceful%20smile%2C%20wearing%20simple%20white%20shirt%2C%20soft%20natural%20lighting%2C%20serene%20inspirational%20background%2C%20faith%20based%20leader%20headshot%20photography&width=400&height=400&seq=team-director-2&orientation=squarish',
      social: { instagram: '@pastordanielvega', facebook: 'facebook.com/pastordanielvega' },
    },
    locutores: [
      {
        id: 2,
        name: 'Elena Ruiz',
        role: 'Locutora Principal',
        bio: 'Voz de la mañana y las tardes. Elena transmite paz y alegría en cada programa, conectando con la audiencia a través de la música cristiana.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20female%20Christian%20radio%20host%20portrait%2C%20gentle%20smile%2C%20wearing%20modest%20elegant%20attire%2C%20soft%20natural%20lighting%2C%20peaceful%20studio%20background%2C%20inspirational%20media%20portrait%20photography&width=400&height=400&seq=team-host-4&orientation=squarish',
        social: { instagram: '@elenaruiz', facebook: 'facebook.com/elenaruiz' },
      },
      {
        id: 3,
        name: 'David Herrera',
        role: 'Locutor',
        bio: 'Encargado de la noche de oración. David guía a la audiencia en momentos de reflexión y conexión espiritual.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Calm%20spiritual%20male%20radio%20host%20portrait%2C%20warm%20kind%20expression%2C%20wearing%20simple%20casual%20attire%2C%20soft%20warm%20lighting%2C%20serene%20background%2C%20peaceful%20media%20portrait%20photography&width=400&height=400&seq=team-host-5&orientation=squarish',
        social: { instagram: '@davidherrera', facebook: 'facebook.com/davidherrera' },
      },
    ],
    productores: [
      {
        id: 4,
        name: 'María Castañeda',
        role: 'Productora de Contenido',
        bio: 'Encargada de la programación musical y contenido familiar. María selecciona las alabanzas que tocan los corazones de la audiencia.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20caring%20female%20producer%20portrait%2C%20genuine%20smile%2C%20wearing%20professional%20casual%20attire%2C%20bright%20natural%20lighting%2C%20clean%20modern%20background%2C%20media%20production%20headshot%20photography&width=400&height=400&seq=team-producer-3&orientation=squarish',
        social: { instagram: '@mariacastaneda', facebook: 'facebook.com/mariacastaneda' },
      },
    ],
    invitados: [
      {
        id: 5,
        name: 'Coro Celestial',
        role: 'Coro Invitado',
        bio: 'Coro gospel que participa en programas especiales y transmisiones en vivo. Su armonía y devoción elevan cada momento.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Gospel%20choir%20group%20portrait%20with%20warm%20joyful%20expressions%2C%20diverse%20group%20in%20elegant%20robes%2C%20church%20interior%20with%20stained%20glass%20background%2C%20inspirational%20group%20photography%20with%20soft%20golden%20lighting&width=400&height=400&seq=team-guest-3&orientation=squarish',
        social: { facebook: 'facebook.com/corocelestial' },
      },
    ],
  },
  3: {
    director: {
      id: 1,
      name: 'Raúl Hernández',
      role: 'Director General',
      bio: 'Fundador de La Mexicana FM. Apasionado de la música regional mexicana, Raúl ha dedicado su vida a preservar y promover la cultura musical de México en Estados Unidos.',
      photoUrl: 'https://readdy.ai/api/search-image?query=Confident%20Hispanic%20male%20director%20portrait%20with%20warm%20charisma%2C%20wearing%20traditional%20Mexican%20embroidered%20shirt%2C%20warm%20studio%20lighting%20with%20amber%20tones%2C%20cultural%20background%20elements%2C%20media%20executive%20portrait%20photography&width=400&height=400&seq=team-director-3&orientation=squarish',
      social: { instagram: '@raulhernandez', facebook: 'facebook.com/raulhernandez' },
    },
    locutores: [
      {
        id: 2,
        name: 'Alejandro Ramírez',
        role: 'Locutor Principal',
        bio: 'La voz del sabor mexicano. Alejandro lleva 10 años en la cabina y es conocido por su carisma, humor y profundo conocimiento de la música regional.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20male%20radio%20host%20in%20studio%20with%20warm%20charismatic%20expression%2C%20wearing%20black%20shirt%20and%20professional%20headphones%2C%20studio%20with%20Mexican%20decorative%20elements%20and%20warm%20amber%20lighting%2C%20broadcast%20portrait&width=400&height=400&seq=team-host-6&orientation=squarish',
        social: { instagram: '@alejandroramirez', facebook: 'facebook.com/alejandroramirez' },
      },
      {
        id: 3,
        name: 'María Flores',
        role: 'Locutora',
        bio: 'Especialista en música norteña y banda. María trae la energía y pasión de la música norteña a cada transmisión.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20female%20Mexican%20radio%20host%20portrait%20with%20joyful%20expression%2C%20wearing%20colorful%20traditional%20attire%2C%20festive%20studio%20background%20with%20warm%20lighting%2C%20cultural%20media%20portrait%20photography&width=400&height=400&seq=team-host-7&orientation=squarish',
        social: { instagram: '@mariaflores', facebook: 'facebook.com/mariaflores' },
      },
    ],
    productores: [
      {
        id: 4,
        name: 'Pedro Gómez',
        role: 'Productor de Eventos',
        bio: 'Encargado de organizar los eventos en vivo y conciertos. Pedro lleva la fiesta de la radio a la calle con sus producciones.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20male%20event%20producer%20portrait%2C%20energetic%20friendly%20expression%2C%20wearing%20casual%20smart%20attire%2C%20colorful%20event%20setup%20background%20with%20lights%2C%20creative%20industry%20portrait%20photography&width=400&height=400&seq=team-producer-4&orientation=squarish',
        social: { instagram: '@pedrogomez', facebook: 'facebook.com/pedrogomez' },
      },
    ],
    invitados: [
      {
        id: 5,
        name: 'Mariachi Sol de México',
        role: 'Mariachi Invitado',
        bio: 'Mariachi tradicional que participa en transmisiones especiales y eventos culturales en vivo desde la cabina.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Traditional%20Mexican%20mariachi%20band%20group%20portrait%20with%20charro%20suits%20and%20sombreros%2C%20colorful%20festive%20background%2C%20warm%20cultural%20atmosphere%2C%20Mexican%20heritage%20group%20photography%20with%20dramatic%20lighting&width=400&height=400&seq=team-guest-4&orientation=squarish',
        social: { facebook: 'facebook.com/mariachisol' },
      },
    ],
  },
  5: {
    director: {
      id: 1,
      name: 'Diana Ríos',
      role: 'Directora y Fundadora',
      bio: 'Fundadora de Rock Total. Diana es una pionera del rock en Los Ángeles, dedicada a promover bandas emergentes y mantener viva la escena rockera.',
      photoUrl: 'https://readdy.ai/api/search-image?query=Female%20rock%20radio%20DJ%20director%20with%20edgy%20style%2C%20wearing%20leather%20jacket%20and%20headphones%2C%20confident%20expression%2C%20studio%20with%20rock%20band%20posters%20and%20guitars%20in%20background%2C%20warm%20red%20and%20amber%20lighting%2C%20broadcast%20portrait&width=400&height=400&seq=team-director-5&orientation=squarish',
      social: { instagram: '@dianarios', facebook: 'facebook.com/dianarios' },
    },
    locutores: [
      {
        id: 2,
        name: 'Kevin Stone',
        role: 'Locutor',
        bio: 'Especialista en rock clásico y metal. Kevin trae la nostalgia de los grandes clásicos del rock a la audiencia.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Male%20rock%20music%20fan%20host%20portrait%20with%20edgy%20style%2C%20wearing%20band%20t-shirt%20and%20headphones%2C%20confident%20attitude%2C%20dark%20studio%20background%20with%20rock%20memorabilia%2C%20moody%20rock%20media%20portrait%20photography&width=400&height=400&seq=team-host-8&orientation=squarish',
        social: { instagram: '@kevinstone', facebook: 'facebook.com/kevinstone' },
      },
      {
        id: 3,
        name: 'Lorena Vázquez',
        role: 'Locutora',
        bio: 'Voz del rock alternativo. Lorena descubre y promueve las bandas indie que están revolucionando la escena.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Alternative%20style%20female%20radio%20host%20portrait%20with%20dark%20hair%20and%20edgy%20makeup%2C%20confident%20expression%2C%20wearing%20leather%20jacket%2C%20dark%20moody%20studio%20lighting%2C%20rock%20music%20media%20portrait%20photography&width=400&height=400&seq=team-host-9&orientation=squarish',
        social: { instagram: '@lorenavazquez', facebook: 'facebook.com/lorenavazquez' },
      },
    ],
    productores: [
      {
        id: 4,
        name: 'Alex Rockero',
        role: 'Productor Musical',
        bio: 'Encargado de la curación musical y descubrimiento de nuevos talentos. Alex es la conexión entre Rock Total y las bandas emergentes.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Creative%20male%20music%20producer%20with%20alternative%20style%2C%20wearing%20band%20t-shirt%2C%20confident%20friendly%20expression%2C%20studio%20background%20with%20musical%20equipment%2C%20warm%20creative%20lighting%2C%20music%20industry%20portrait%20photography&width=400&height=400&seq=team-producer-5&orientation=squarish',
        social: { instagram: '@alexrockero', facebook: 'facebook.com/alexrockero' },
      },
    ],
    invitados: [
      {
        id: 5,
        name: 'Banda Emergente LA',
        role: 'Banda Invitada',
        bio: 'Banda emergente de Los Ángeles que ha participado en sesiones en vivo y entrevistas exclusivas en Rock Total.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Rock%20band%20group%20portrait%20in%20rehearsal%20space%2C%20four%20members%20with%20edgy%20alternative%20style%2C%20moody%20atmospheric%20lighting%20with%20blue%20and%20purple%20tones%2C%20garage%20rock%20aesthetic%2C%20creative%20group%20photography&width=400&height=400&seq=team-guest-5&orientation=squarish',
        social: { instagram: '@bandaemergente', facebook: 'facebook.com/bandaemergente' },
      },
    ],
  },
  7: {
    director: {
      id: 1,
      name: 'Fernando Gutiérrez',
      role: 'Director y Fundador',
      bio: 'Fundador de Deportes Total. Exjugador de fútbol convertido en periodista deportivo, Fernando ha creado una de las radios deportivas más respetadas del país.',
      photoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20male%20host%20in%20dynamic%20studio%20with%20sporty%20casual%20look%2C%20energetic%20expression%2C%20studio%20with%20sports%20memorabilia%20and%20screens%2C%20professional%20broadcast%20portrait&width=400&height=400&seq=team-director-7&orientation=squarish',
      social: { instagram: '@fernandogutierrez', facebook: 'facebook.com/fernandogutierrez' },
    },
    locutores: [
      {
        id: 2,
        name: 'Sofía Martínez',
        role: 'Locutora',
        bio: 'Especialista en fútbol femenino y análisis táctico. Sofía aporta una perspectiva única y detallada al análisis deportivo.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Confident%20female%20sports%20analyst%20portrait%20with%20sporty%20casual%20look%2C%20friendly%20smile%2C%20wearing%20team%20polo%20shirt%2C%20professional%20studio%20background%20with%20sports%20elements%2C%20dynamic%20media%20portrait%20photography&width=400&height=400&seq=team-host-10&orientation=squarish',
        social: { instagram: '@sofiamartinez', facebook: 'facebook.com/sofiamartinez' },
      },
      {
        id: 3,
        name: 'Memo López',
        role: 'Locutor',
        bio: 'El experto en datos y estadísticas. Memo complementa el análisis con números, curiosidades y anécdotas del deporte.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Intelligent%20male%20sports%20journalist%20portrait%20with%20analytical%20look%2C%20wearing%20casual%20smart%20attire%2C%20glasses%2C%20professional%20office%20background%20with%20sports%20screens%2C%20media%20industry%20headshot%20photography&width=400&height=400&seq=team-host-11&orientation=squarish',
        social: { instagram: '@memolopez', facebook: 'facebook.com/memolopez' },
      },
    ],
    productores: [
      {
        id: 4,
        name: 'Karla Paredes',
        role: 'Productora de Cobertura',
        bio: 'Encargada de la cobertura en vivo desde estadios y eventos deportivos. Karla coordina la transmisión en el terreno de juego.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20female%20sports%20producer%20portrait%20with%20energetic%20look%2C%20wearing%20casual%20sporty%20attire%2C%20stadium%20background%20with%20bright%20lights%2C%20professional%20sports%20media%20portrait%20photography&width=400&height=400&seq=team-producer-6&orientation=squarish',
        social: { instagram: '@karlaparedes', facebook: 'facebook.com/karlaparedes' },
      },
    ],
    invitados: [
      {
        id: 5,
        name: 'Roberto Deportivo',
        role: 'Exfutbolista Invitado',
        bio: 'Exfutbolista profesional que aporta su experiencia en el campo al análisis de La Mesa Redonda.',
        photoUrl: 'https://readdy.ai/api/search-image?query=Former%20male%20professional%20athlete%20portrait%20with%20confident%20athletic%20look%2C%20wearing%20casual%20polo%20shirt%2C%20sports%20facility%20background%2C%20warm%20natural%20lighting%2C%20sports%20media%20portrait%20photography&width=400&height=400&seq=team-guest-6&orientation=squarish',
        social: { instagram: '@robertodeportivo', facebook: 'facebook.com/robertodeportivo' },
      },
    ],
  },
};

export function getRadioProfile(stationId: number): RadioProfile | undefined {
  return radioProfiles[stationId];
}

export function getRadioTeam(stationId: number): RadioTeam | undefined {
  return radioTeams[stationId];
}